import {
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import Filter from "../Filter";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_UPLOAD_LOGS_ENDPOINT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

const COLUMNS = [
  { id: "date",        label: "Date",        minWidth: 120 },
  { id: "source",      label: "Source",      minWidth: 120 },
  { id: "file_type",   label: "File Type",   minWidth: 140 },
  { id: "file_name",   label: "File Name",   minWidth: 220 },
  { id: "uploaded_by_name", label: "Uploaded By", minWidth: 160 },
  {
    id: "created_at",
    label: "Uploaded At",
    minWidth: 170,
    render: (row) => formatWithTzLabel(row.created_at),
  },
  {
    id: "status",
    label: "Status",
    minWidth: 110,
    render: (row) =>
      row.status === "Success" ? (
        <Chip variant="outlined" color="success" label="Success" size="small" />
      ) : (
        <Chip variant="outlined" color="error" label="Failed" size="small" />
      ),
  },
  { id: "message", label: "Message", minWidth: 280 },
];

const FILTER_FIELDS = [
  { label: "Source", type: "single_select_list", values: ["RedVision", "iWell", "Bonanza"] },
  { label: "File Type", type: "single_select_list", values: ["Clients", "Daily Holdings"] },
  { label: "Status", type: "single_select_list", values: ["Success", "Failed"] },
  { label: "Date", type: "date" },
];


const FileUploadLogs = () => {
  const { getValidToken } = useAuth();
  const [logs, setLogs]               = useState([]);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount]   = useState(0);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [filters, setFilters]         = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });

    if (search.trim()) {
      params.set("search", search.trim());
    }

    filters.forEach(({ field, operator, value }) => {
      params.append(field.toLowerCase().replaceAll(" ", "_"), `${operator.toLowerCase().replaceAll(" ", "_")}:${value ?? ""}`);
    });

    try {
      const token  = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const res = await fetch(`${MFD_UPLOAD_LOGS_ENDPOINT}?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch upload logs");
      const data = await res.json();
      setLogs(data.results ?? []);
      setTotalCount(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching upload logs:", err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, filters, getValidToken]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, }} >
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: { xs: "stretch", sm: "center" },flexDirection: { xs: "column", sm: "row" }, }}>
        <Box sx={{ flex: 1, maxWidth: { xs: "100%", md: 500 }, width: "100%", }}>
          <TableSearchInput
          searchQuery={search}
          onSearchChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder={
            isMobile
            ? "Search..."
            : "Search by Source, File Type, File Name, Uploaded By or Message"
            }
            />
        </Box>
        <Filter
          fields={FILTER_FIELDS}
          onApply={(newFilters) => {
            setFilters(newFilters);
            setPage(0);
          }}
          initialFilters={filters}
        />
      </Box>

      <AppTable
        columns={COLUMNS}
        rows={logs}
        rowKey="id"
        loading={loading}
        emptyMessage="No logs found"
        maxHeight={620}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto", width: "100%" }}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
};

export default FileUploadLogs;
